import { useTranslation } from "react-i18next";
import { ArrowRight, Check, X } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { scrollToSection } from "@/lib/scrollToSection";

/**
 * Anti-SaaS positioning section: puts what a standard SaaS forces on a company
 * directly next to what an owned, bespoke tool does instead. Two hairline
 * columns, no nested cards — the contrast carries the argument.
 */
const SaasVsCustomSection = () => {
  const { t } = useTranslation();
  const left = t("saasVs.left", { returnObjects: true }) as string[];
  const right = t("saasVs.right", { returnObjects: true }) as string[];

  return (
    <section
      id="saas-vs-custom"
      className="relative overflow-hidden border-t border-border bg-background section-y-tight"
    >
      <div className="absolute inset-0 tech-dots pointer-events-none opacity-[0.07]" />

      <div className="container relative">
        <ScrollReveal className="max-w-3xl">
          <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-foreground">
            {t("saasVs.title")}
          </h2>
          <p className="mt-4 md:mt-6 max-w-2xl text-[15px] md:text-lg leading-relaxed text-muted-foreground">
            {t("saasVs.body")}
          </p>
        </ScrollReveal>

        <ScrollReveal delay={120}>
          <div className="mt-10 md:mt-14 grid gap-8 md:grid-cols-2 md:gap-0">
            {/* Standard SaaS — muted, the thing being replaced */}
            <div className="md:pr-10">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  {t("saasVs.leftLabel")}
                </span>
                <X size={14} className="text-muted-foreground" aria-hidden />
              </div>
              <ul className="divide-y divide-border/60">
                {left.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 py-4 text-[15px] leading-relaxed text-muted-foreground"
                  >
                    <X size={15} className="mt-1 shrink-0 text-muted-foreground/70" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Your own tool — accent side */}
            <div className="relative md:border-l md:border-border md:pl-10">
              <div className="flex items-center justify-between border-b border-accent/40 pb-3">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
                  {t("saasVs.rightLabel")}
                </span>
                <Check size={14} className="text-accent" aria-hidden />
              </div>
              <ul className="divide-y divide-border/60">
                {right.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 py-4 text-[15px] leading-relaxed text-foreground"
                  >
                    <Check size={15} className="mt-1 shrink-0 text-accent" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="mt-10 md:mt-14 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-6">
            <span className="hover-glow-accent inline-block w-full sm:w-auto">
              <a
                href="#blueprint"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection("blueprint");
                }}
                className="group inline-flex w-full items-center justify-center gap-2 btn-clip bg-primary px-6 py-3.5 font-medium text-primary-foreground btn-label-contrast transition-transform duration-200 active:scale-[0.97] sm:w-auto md:px-7"
              >
                {t("saasVs.cta")}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </span>
            <span className="mono-label text-muted-foreground">{t("saasVs.footnote")}</span>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default SaasVsCustomSection;
